const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const createTextValidator = (
  fieldLabel,
  { min, max, maxInEdit, requiredInCreate = false, requiredInEdit = false },
) => {
  return (value, mode = "create") => {
    const len = value?.trim()?.length || 0;

    if (
      (requiredInCreate && mode === "create" && !value) ||
      (requiredInEdit && mode === "edit" && !value)
    ) {
      return `${fieldLabel} is required`;
    }

    if (min && len > 0 && len < min) {
      return `${fieldLabel} must be at least ${min} characters`;
    }

    const effectiveMax = mode === "edit" && maxInEdit ? maxInEdit : max;
    if (len > effectiveMax) {
      return `${fieldLabel} must not exceed ${effectiveMax} characters`;
    }

    return null;
  };
};

export const createMaxOnlyValidator = (fieldLabel, max) => {
  return (value) => {
    const len = value?.trim()?.length || 0;

    if (len > max) {
      return `${fieldLabel} must not exceed ${max} characters`;
    }

    return null;
  };
};

export const createSelectValidator = (fieldLabel, max, { requiredInEdit = false } = {}) => {
  return (value, mode = "create") => {
    if ((mode === "create" && !value) || (requiredInEdit && mode === "edit" && !value)) {
      return `${fieldLabel} is required`;
    }

    if (value?.length > max) {
      return `${fieldLabel} must not exceed ${max} characters`;
    }

    return null;
  };
};

/**
 * Creates a validator that checks if field is required, optionally in edit mode too
 * @param {string} fieldLabel - Human-readable field name for error messages
 */
export const createRequiredOnlyValidator = (
  fieldLabel,
  { requiredInEdit = false } = {},
) => {
  return (value, mode = "create") => {
    if (
      (mode === "create" && !value) ||
      (requiredInEdit && mode === "edit" && !value)
    ) {
      return `${fieldLabel} is required`;
    }

    return null;
  };
};

export const createEmailValidator = (
  fieldLabel,
  { max, invalidMessage = "Invalid email address", requiredInEdit = false },
) => {
  return (value, mode = "create") => {
    if (
      (mode === "create" && !value) ||
      (requiredInEdit && mode === "edit" && !value)
    ) {
      return `${fieldLabel} is required`;
    }

    if (value?.length > max) {
      return `${fieldLabel} must not exceed ${max} characters`;
    }

    if (value && !EMAIL_REGEX.test(value)) {
      return invalidMessage;
    }

    return null;
  };
};

export const createPhoneValidator = (fieldLabel, { requiredInEdit = false } = {}) => {
  return (value, mode = "create") => {
    if (
      (mode === "create" && !value) ||
      (requiredInEdit && mode === "edit" && !value)
    ) {
      return `${fieldLabel} is required`;
    }

    if (value) {
      const trimmed = value.trim();

      if (!/^\d+$/.test(trimmed)) {
        return `${fieldLabel} must contain digits only`;
      }

      if (!trimmed.startsWith("254")) {
        return `${fieldLabel} must start with '254'`;
      }

      if (trimmed.length !== 12) {
        return `${fieldLabel} must be exactly 12 digits (e.g., 2547XXXXXXXX)`;
      }
    }

    return null;
  };
};

export const createOptionalSelectValidator = () => {
  return () => null;
};

export const createObjectValidator = (
  fieldLabel,
  { requiredInEdit = false } = {},
) => {
  return (value, mode = "create") => {
    // value may be a plain enum string (as returned by GET) or an
    // { code, description } object (as set by ObjectSelect on change).
    const isEmpty = !value || (typeof value === "object" && !value.code);

    if (
      (mode === "create" && isEmpty) ||
      (requiredInEdit && mode === "edit" && isEmpty)
    ) {
      return `${fieldLabel} is required`;
    }

    return null;
  };
};

export const merchantValidators = {
  merchantName: createTextValidator("Merchant name", {
    min: 3,
    max: 250,
    requiredInCreate: true,
    requiredInEdit: true,
  }),
  registrationNumber: createTextValidator("Registration number", {
    min: 3,
    max: 50,
    requiredInCreate: true,
    requiredInEdit: true,
  }),
  nationality: createTextValidator("Nationality", {
    min: 3,
    max: 50,
    requiredInCreate: true,
    requiredInEdit: true,
  }),
  address: createTextValidator("Address", {
    min: 5,
    max: 200,
    requiredInCreate: true,
    requiredInEdit: true,
  }),
  merchantAdminFirstName: createTextValidator("Admin first name", {
    min: 3,
    max: 50,
    requiredInCreate: true,
  }),
  merchantAdminLastName: createTextValidator("Admin last name", {
    min: 3,
    max: 50,
    requiredInCreate: true,
  }),
  merchantAlias: createTextValidator("Merchant alias", {
    min: 3,
    max: 20,
    maxInEdit: 250,
  }),
  city: createTextValidator("City", { min: 3, max: 50 }),
  county: createTextValidator("County", { min: 3, max: 50 }),
  taxPin: createTextValidator("Tax PIN", { min: 3, max: 20 }),
  directors: createMaxOnlyValidator("Directors", 250),
  shareholders: createMaxOnlyValidator("Shareholders", 250),
  country: createSelectValidator("Country", 50, { requiredInEdit: true }),
  merchantType: createSelectValidator("Merchant type", 50, {
    requiredInEdit: true,
  }),
  merchantSegment: createOptionalSelectValidator(),
  currency: createSelectValidator("Currency", 4, { requiredInEdit: true }),
  registrationDate: createRequiredOnlyValidator("Registration date", {
    requiredInEdit: true,
  }),
  email: createEmailValidator("Email", { max: 100, requiredInEdit: true }),
  merchantAdminEmail: createEmailValidator("Admin email", {
    max: 50,
    invalidMessage: "Enter a valid admin email address",
  }),
  phoneNumber: createPhoneValidator("Phone number", { requiredInEdit: true }),
  merchantAdminPhoneNumber: createPhoneValidator("Admin phone number"),
  economicSector: createObjectValidator("Economic sector", {
    requiredInEdit: true,
  }),
  gender: createObjectValidator("Gender", { requiredInEdit: true }),
  productTransactionCode: createObjectValidator("Product transaction code", {
    requiredInEdit: true,
  }),
  productOwnership: createObjectValidator("Product ownership", {
    requiredInEdit: true,
  }),
  productCategory: createObjectValidator("Product category", {
    requiredInEdit: true,
  }),
};

export default merchantValidators;
